import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { USES_NON_ESSENTIAL_COOKIES } from '../data/launch';
import { applyConsent, getConsent, setConsent } from '../utils/consent';
import type { ConsentChoice } from '../utils/consent';

/**
 * Consent banner for non-essential technologies.
 *
 * While USES_NON_ESSENTIAL_COOKIES is false the website loads no analytics or
 * marketing scripts, sets only strictly necessary storage, no consent is
 * required, and this renders nothing.
 *
 * The banner never loads anything itself — it only records the decision and
 * hands it to applyConsent() in utils/consent.ts, which is the single gate for
 * loading and tearing down non-essential scripts.
 */
export function CookieBanner() {
  const [choice, setChoice] = useState<ConsentChoice | null | 'pending'>('pending');
  useEffect(() => {
    if (!USES_NON_ESSENTIAL_COOKIES) return;
    const stored = getConsent();
    setChoice(stored);
    // Re-apply a previously stored decision on every page load.
    applyConsent(stored);
  }, []);
  if (!USES_NON_ESSENTIAL_COOKIES) return null;
  const record = (value: ConsentChoice) => {
    setConsent(value);
    setChoice(value);
  };
  return <AnimatePresence data-id="element-21" data-mp="ot1moa8ur7mm">
      {choice === null && <motion.div initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 16
    }} transition={{
      duration: 0.25,
      ease: [0.22, 1, 0.36, 1]
    }} role="dialog" aria-label="Cookie choices" className="fixed bottom-4 left-4 right-4 z-[60] mx-auto max-w-3xl rounded-2xl bg-gray-950 text-white p-5 sm:p-6 shadow-2xl border border-white/[0.08]" data-id="element-22" data-mp="tuqwxno93h9n">
          <div className="sm:flex sm:items-center sm:gap-6" data-id="element-23" data-mp="1cm9w1jf5mb6e">
            <p className="text-sm text-gray-300 leading-relaxed mb-4 sm:mb-0" data-id="element-24" data-mp="3sa08aorxz437">
              We use strictly necessary storage to run this website. With your
              permission we would also use analytics to understand how the site
              is used. Read our{' '}
              <Link to="/cookie-policy" className="font-semibold text-kitty-yellow underline underline-offset-2" data-id="element-25" data-mp="396q40sbac2wx">
                Cookie Policy
              </Link>
              .
            </p>
            <div className="flex flex-shrink-0 gap-3" data-id="element-26" data-mp="2zf2gh2hun60q">
              <button type="button" onClick={() => record('rejected')} className="flex-1 sm:flex-none rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow" data-id="element-27" data-mp="2uiby28iknyc5">
                Reject non-essential
              </button>
              <button type="button" onClick={() => record('accepted')} className="flex-1 sm:flex-none rounded-full bg-kitty-yellow px-5 py-2.5 text-sm font-bold text-kitty-dark transition-colors duration-150 hover:bg-kitty-yellow-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-28" data-mp="jpiolsch0ijs">
                Accept all
              </button>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}